// The surface of a small body drawn from its real shape (src/smallbodies/).
// A MeshStandardMaterial patched in place, so the Sun's point light, the
// exposure and the tone mapping treat it like every other world, with:
//
// - albedo read by direction from the body's own map (planetocentric
//   longitude and latitude of the point, so the map lines up with the shape
//   it was made on), seam-safe, relative to the body's mean and scaled by its
//   measured albedo (displayAlbedo in scripts/build-small-bodies.mjs)
// - the map's own fine shading turned into relief where the mesh is coarser
//   than the map, and below the map's texel a procedural surface: boulders on
//   the rubble piles (Bennu, Ryugu, Apophis), craters on the cratered bodies,
//   each scale fading out before it is smaller than a few pixels, with the
//   boulders' and craters' own shadows
// - where no spacecraft has imaged the surface (Pallas, Hygiea, Apophis, the
//   polar caps of a map that stops short) the procedural surface at every
//   scale: an artistic rendition
// - cast shadows over the whole body, marched through the body's radius by
//   direction (src/smallbodies/radiusmap.js), so Eros's saddle and Vesta's
//   south basin darken where the shape hides the Sun
// - a Lommel-Seeliger share in the diffuse light: dark regolith keeps its
//   brightness out to the limb instead of fading like a painted ball
//
// GLSL rule: no struct ternaries and none of ES 3.00's reserved words.

import * as THREE from 'three';

export const SMALL_BODY_RUBBLE = 0;
export const SMALL_BODY_REGOLITH = 1;
const MAX_OCTAVES = 6;

const VERTEX_PARS = /* glsl */ `
uniform float sbScale;
varying vec3 vSbPos;
varying vec3 vSbNormal;
`;

const VERTEX_MAIN = /* glsl */ `
vSbPos = position / sbScale;
vSbNormal = objectNormal;
`;

const FRAGMENT_PARS = /* glsl */ `
varying vec3 vSbPos;
varying vec3 vSbNormal;
uniform sampler2D sbRadius;
#if SB_MAP
uniform sampler2D sbMap;
#endif
uniform vec3 sbAlbedo;
uniform float sbRelMax;
uniform vec2 sbValidLat;
uniform float sbReach;
uniform float sbGrow;
uniform vec3 sbSunObj;
uniform vec3 sbSunView;
uniform mat3 sbObjToView;
uniform float sbLS;
uniform float sbBump;
uniform float sbSeed;
uniform float sbCells[${MAX_OCTAVES}];
uniform float sbMapTexel;
uniform float sbMottle;

const float SB_TAU = 6.28318530718;
const float SB_PI = 3.14159265359;

vec3 sbHash3(vec3 p) {
  p = fract(p * vec3(0.1031, 0.1030, 0.0973));
  p += dot(p, p.yxz + 33.33);
  return fract((p.xxy + p.yxx) * p.zyx);
}

float sbValue(vec3 p) {
  vec3 i = floor(p);
  vec3 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = sbHash3(i).x;
  float b = sbHash3(i + vec3(1.0, 0.0, 0.0)).x;
  float c = sbHash3(i + vec3(0.0, 1.0, 0.0)).x;
  float d = sbHash3(i + vec3(1.0, 1.0, 0.0)).x;
  float e = sbHash3(i + vec3(0.0, 0.0, 1.0)).x;
  float g = sbHash3(i + vec3(1.0, 0.0, 1.0)).x;
  float h = sbHash3(i + vec3(0.0, 1.0, 1.0)).x;
  float k = sbHash3(i + vec3(1.0, 1.0, 1.0)).x;
  return mix(mix(mix(a, b, f.x), mix(c, d, f.x), f.y), mix(mix(e, g, f.x), mix(h, k, f.x), f.y), f.z);
}

// Value noise and its gradient (yzw).
vec4 sbNoiseD(vec3 x) {
  vec3 i = floor(x);
  vec3 f = fract(x);
  vec3 u = f * f * (3.0 - 2.0 * f);
  vec3 du = 6.0 * f * (1.0 - f);
  float a = sbHash3(i).x;
  float b = sbHash3(i + vec3(1.0, 0.0, 0.0)).x;
  float c = sbHash3(i + vec3(0.0, 1.0, 0.0)).x;
  float d = sbHash3(i + vec3(1.0, 1.0, 0.0)).x;
  float e = sbHash3(i + vec3(0.0, 0.0, 1.0)).x;
  float g = sbHash3(i + vec3(1.0, 0.0, 1.0)).x;
  float h = sbHash3(i + vec3(0.0, 1.0, 1.0)).x;
  float k = sbHash3(i + vec3(1.0, 1.0, 1.0)).x;
  float k1 = b - a;
  float k2 = c - a;
  float k3 = e - a;
  float k4 = a - b - c + d;
  float k5 = a - c - e + h;
  float k6 = a - b - e + g;
  float k7 = -a + b + c - d + e - g - h + k;
  float v = a + k1 * u.x + k2 * u.y + k3 * u.z + k4 * u.x * u.y + k5 * u.y * u.z + k6 * u.z * u.x + k7 * u.x * u.y * u.z;
  vec3 dv = du * vec3(
    k1 + k4 * u.y + k6 * u.z + k7 * u.y * u.z,
    k2 + k5 * u.z + k4 * u.x + k7 * u.z * u.x,
    k3 + k6 * u.x + k5 * u.y + k7 * u.x * u.y);
  return vec4(v, dv);
}

float sbRadiusAt(vec3 d, float lod) {
  float lon = atan(-d.z, d.x);
  float lat = asin(clamp(d.y, -1.0, 1.0));
  return textureLod(sbRadius, vec2(lon / SB_TAU + 1.0, 0.5 - lat / SB_PI), lod).r;
}

// Sunlight reaching p past the rest of the body: march toward the Sun and
// keep the lowest clearance angle over the surface below each step, read
// from the radius map's level whose texel matches the step. Each pixel
// starts the march at its own point within the first step (interleaved
// gradient noise), so the steps' contours along a shadow's soft edge become
// fine grain instead of stacked ledges.
float sbCastShadow(vec3 p, vec3 L) {
  float lit = 1.0;
  float jitter = fract(52.9829189 * fract(dot(gl_FragCoord.xy, vec2(0.06711056, 0.00583715))));
  float t = 0.012 * pow(sbGrow, jitter);
  float texel = SB_TAU / float(textureSize(sbRadius, 0).x);
  for (int i = 0; i < SB_STEPS; i++) {
    vec3 q = p + L * t;
    float rq = length(q);
    if (rq > sbReach) break;
    float lod = max(0.0, log2(t * (sbGrow - 1.0) / (texel * rq)));
    float h = rq - sbRadiusAt(q / rq, lod);
    lit = min(lit, smoothstep(-0.012, 0.012, (h + 0.003) / t));
    if (lit < 0.004) break;
    t *= sbGrow;
  }
  return lit;
}

#if SB_STYLE == 0
// Boulders: angular rocks half sunk in the regolith, one candidate a cell
// whose centre lies within half a cell of the surface. Each stands on an
// ellipse turned its own way, cut into five to eight faces round a tilted
// flat top, with a lower and steeper tier at its foot. The point takes the
// faces of the highest rock over it (and that rock's own albedo, through
// cover) and the shadows of the rocks between it and the Sun, which soften
// and fade along their length so none ends square.
void sbOctave(vec3 p, vec3 n, vec3 L, float cell, float w, float seed, inout vec3 grad, inout float alb, inout float shade, inout float cover) {
  vec3 q = p / cell;
  vec3 ip = floor(q);
  vec3 fp = q - ip;
  float top = 0.0;
  vec3 topGrad = vec3(0.0);
  float topAlb = 1.0;
  float lit = 1.0;
  for (int z = -1; z <= 1; z++)
  for (int y = -1; y <= 1; y++)
  for (int x = -1; x <= 1; x++) {
    vec3 o = vec3(float(x), float(y), float(z));
    vec3 h = sbHash3(ip + o + seed);
    if (h.z > 0.62) continue;
    vec3 c = o + h - fp;
    float cn = dot(c, n);
    float band = 1.0 - smoothstep(0.34, 0.5, abs(cn));
    if (band <= 0.0) continue;
    vec3 h2 = sbHash3(ip + o + seed + 31.7);
    float r = (0.1 + 0.3 * h.x * h.x) * band;
    vec3 t1 = normalize(cross(n, h2 - 0.5));
    vec3 t2 = cross(n, t1);
    float el = 0.55 + 0.45 * h2.y;
    float a = 0.45 + 0.5 * h2.z;
    float sink = (h.z * 0.56 - 0.25) * r;
    vec3 ct = c - cn * n;
    vec2 pl = vec2(-dot(ct, t1), -dot(ct, t2) / el);
    float d2 = dot(pl, pl);
    float r2 = r * r;
    if (d2 < r2) {
      float hgt = sink + a * sqrt(r2 - d2);
      if (hgt > top) {
        top = hgt;
        float faces = 5.0 + floor(h2.x * 4.0);
        float sec = SB_TAU / faces;
        float th = atan(pl.y, pl.x) / sec + h2.y;
        float face = floor(th);
        float thq = (face + 0.5 - h2.y) * sec;
        vec2 dir = vec2(cos(thq), sin(thq));
        float dp = dot(pl, dir);
        vec3 fh = sbHash3(vec3(face, seed, h.x * 97.0));
        if (dp < 0.4 * r) {
          topGrad = a * 0.4 * ((h2.x - 0.5) * t1 + (h2.z - 0.5) * t2);
        } else {
          float slope = a * mix(0.7, 1.7, step(0.8 * r, dp)) * (0.8 + 0.4 * fh.x);
          topGrad = -slope * (dir.x * t1 + dir.y * t2 / el);
        }
        topAlb = (0.72 + 0.62 * h.y * h.y) * (0.9 + 0.2 * fh.y);
      }
    } else {
      float rs = r * sqrt(el * mix(a, 1.0, 0.4));
      vec3 cs = ct + (sink + a * r - rs) * n;
      float tc = dot(cs, L);
      if (tc > 0.0) {
        float miss = length(cs - tc * L) - rs;
        float soft = 0.015 + 0.1 * tc;
        float fade = (1.0 - smoothstep(0.7, 0.98, length(ct))) * (1.0 - smoothstep(0.2, 0.8, tc));
        lit = min(lit, mix(1.0, smoothstep(-soft, soft, miss), fade));
      }
    }
  }
  float on = smoothstep(0.0, 0.03, top);
  grad += w * on * topGrad;
  alb *= mix(1.0, topAlb, w * on);
  shade = min(shade, mix(1.0, lit, w));
  cover = max(cover, w * on);
}
#else
// A crater's height (in rim radii) at u rim radii from its centre: a bowl,
// sharp-rimmed when fresh, a smooth sag with a soft rim when old, and an
// apron falling off outside.
float sbCraterH(float u, float depth, float rim, float fresh) {
  float bowl = 0.0;
  if (u < 1.0) {
    float s1 = 1.0 - u * u;
    bowl = -depth * mix(s1 * s1, s1, fresh);
  }
  float crease = u < 1.0 ? rim * u * u * u * u : rim / (u * u * u);
  float soft = rim * exp(-(u - 1.0) * (u - 1.0) * 11.0);
  return (bowl + mix(soft, crease, fresh)) * (1.0 - smoothstep(1.3, 2.0, u));
}

// Craters: one candidate a cell where a sphere meets the surface, about half
// the cells holding one, small ones commoner than large, most of them old.
// Shadows come from the profile itself, sampled toward the Sun, so an old
// crater only shades at a low Sun and a fresh one fills with shadow.
void sbOctave(vec3 p, vec3 n, vec3 L, float cell, float w, float seed, inout vec3 grad, inout float alb, inout float shade, inout float cover) {
  vec3 q = p / cell;
  vec3 ip = floor(q);
  vec3 fp = q - ip;
  float ln = dot(L, n);
  vec3 lt = L - ln * n;
  float ltl = max(length(lt), 1e-4);
  vec3 az = lt / ltl;
  float tanE = ln / ltl;
  float lit = 1.0;
  for (int z = -1; z <= 1; z++)
  for (int y = -1; y <= 1; y++)
  for (int x = -1; x <= 1; x++) {
    vec3 o = vec3(float(x), float(y), float(z));
    vec3 h = sbHash3(ip + o + seed);
    if (h.z > 0.5) continue;
    vec3 c = o + h - fp;
    float R = 0.16 + 0.34 * h.x * h.x;
    float cn = dot(c, n);
    float rho2 = R * R - cn * cn;
    if (rho2 <= 0.0) continue;
    float rho = sqrt(rho2);
    vec3 ct = c - cn * n;
    float d = length(ct);
    float u = d / rho;
    if (u >= 2.0) continue;
    float fresh = h.y * h.y * h.y;
    float depth = mix(0.1, 0.38, fresh);
    float rim = mix(0.012, 0.065, fresh);
    float h0 = sbCraterH(u, depth, rim, fresh);
    float dH = (sbCraterH(u + 0.01, depth, rim, fresh) - sbCraterH(max(u - 0.01, 0.0), depth, rim, fresh)) / (u + 0.01 - max(u - 0.01, 0.0));
    grad += w * dH * (-ct / max(d, 1e-4));
    alb *= mix(1.0, 1.0 + 0.35 * fresh * (1.0 - smoothstep(0.6, 1.8, u)), w);
    if (tanE > 0.0 && tanE < 1.2 && u < 1.6) {
      vec3 pr = -ct / rho;
      // The rim itself, exactly: where the ray toward the Sun crosses it.
      if (u < 1.0) {
        float qa = dot(pr, az);
        float sr = -qa + sqrt(max(qa * qa - dot(pr, pr) + 1.0, 0.0));
        lit = min(lit, smoothstep(-0.03, 0.03, tanE - (sbCraterH(1.0, depth, rim, fresh) - h0) / max(sr, 1e-3)));
      }
      // The rest of the profile, sampled.
      for (int k = 1; k <= 8; k++) {
        float sk = 0.06 * float(k) + 0.025 * float(k * k);
        vec3 qk = pr + az * sk;
        float hk = sbCraterH(length(qk), depth, rim, fresh);
        lit = min(lit, smoothstep(-0.04, 0.04, tanE - (hk - h0) / sk));
      }
    }
  }
  shade = min(shade, mix(1.0, lit, w));
}
#endif

#if SB_MAP
float sbRelOf(vec4 t) {
#if SB_MAP_GREY
  float v = t.r;
  v = v <= 0.04045 ? v / 12.92 : pow((v + 0.055) / 1.055, 2.4);
  return v * sbRelMax;
#else
  return dot(t.rgb, vec3(0.2126, 0.7152, 0.0722)) * sbRelMax;
#endif
}
#endif
`;

// Runs in place of map_fragment: albedo, the procedural surface and the cast
// shadow, kept in locals the later chunks read.
const FRAGMENT_SURFACE = /* glsl */ `
vec3 sbDir = normalize(vSbPos);
vec3 sbN = normalize(vSbNormal);
float sbLat = asin(clamp(sbDir.y, -1.0, 1.0));
float sbFp = max(length(fwidth(vSbPos)), 1e-6);
vec3 sbRel = vec3(1.0);
vec3 sbRelC = vec3(1.0);
vec3 sbGrad = vec3(0.0);
vec3 sbMapGrad = vec3(0.0);
float sbCov = 0.0;
#if SB_MAP
sbCov = smoothstep(sbValidLat.x, sbValidLat.x + 0.08, sbLat) * (1.0 - smoothstep(sbValidLat.y - 0.08, sbValidLat.y, sbLat));
{
  float lonN = atan(-sbDir.z, sbDir.x) / SB_TAU;
  float v = 0.5 - sbLat / SB_PI;
  vec2 uvA = vec2(fract(lonN), v);
  vec2 uvB = vec2(lonN, v);
  vec2 dxA = dFdx(uvA);
  vec2 dyA = dFdy(uvA);
  vec2 dxB = dFdx(uvB);
  vec2 dyB = dFdy(uvB);
  bool useB = abs(dxA.x) + abs(dyA.x) > abs(dxB.x) + abs(dyB.x);
  vec2 uv = useB ? uvB : uvA;
  vec2 dx = useB ? dxB : dxA;
  vec2 dy = useB ? dyB : dyA;
  vec4 t0 = textureGrad(sbMap, uv, dx, dy);
  // The ground's albedo eight times coarser: what a rock standing on it
  // takes instead of the detail under it.
  vec4 t1 = textureGrad(sbMap, uv, dx * 8.0, dy * 8.0);
#if SB_MAP_GREY
  sbRel = mix(vec3(1.0), vec3(sbRelOf(t0)), sbCov);
  sbRelC = mix(vec3(1.0), vec3(sbRelOf(t1)), sbCov);
#else
  sbRel = mix(vec3(1.0), t0.rgb * sbRelMax, sbCov);
  sbRelC = mix(vec3(1.0), t1.rgb * sbRelMax, sbCov);
#endif
  // The map's shading as relief: its slope by central differences a texel
  // apart (a pixel where the map is minified), so the light stays smooth
  // when a texel is many pixels across.
  if (sbBump > 0.0) {
    vec2 ts = 1.0 / vec2(textureSize(sbMap, 0));
    vec2 hs = max(ts, vec2(max(abs(dx.x), abs(dy.x)), max(abs(dx.y), abs(dy.y))));
    float dLon = sbRelOf(textureGrad(sbMap, uv + vec2(hs.x, 0.0), dx, dy)) - sbRelOf(textureGrad(sbMap, uv - vec2(hs.x, 0.0), dx, dy));
    float dLat = sbRelOf(textureGrad(sbMap, uv - vec2(0.0, hs.y), dx, dy)) - sbRelOf(textureGrad(sbMap, uv + vec2(0.0, hs.y), dx, dy));
    float lon = lonN * SB_TAU;
    float cl = max(cos(sbLat), 0.05);
    vec3 east = vec3(-sin(lon), 0.0, -cos(lon));
    vec3 north = vec3(-sin(sbLat) * cos(lon), cos(sbLat), sin(sbLat) * sin(lon));
    sbMapGrad = sbBump * sbCov * (east * dLon / (2.0 * hs.x * SB_TAU * cl) + north * dLat / (2.0 * hs.y * SB_PI));
  }
}
#endif
// Broad mottling where nothing was imaged.
if (sbMottle > 0.0 && sbCov < 1.0) {
  float m = sbValue(sbDir * 3.0 + sbSeed) * 0.55 + sbValue(sbDir * 9.0 - sbSeed) * 0.3 + sbValue(sbDir * 27.0 + 2.0 * sbSeed) * 0.15;
  float mf = 1.0 + sbMottle * (1.0 - sbCov) * (m - 0.5) * 2.0;
  sbRel *= mf;
  sbRelC *= mf;
}
float sbAlb = 1.0;
float sbShade = 1.0;
float sbCover = 0.0;
for (int k = 0; k < SB_OCTAVES; k++) {
  float cell = sbCells[k];
  float w = smoothstep(2.5, 7.0, cell / sbFp);
  // Where the map has data, only the scales too small for it to show.
  w *= 1.0 - sbCov * smoothstep(2.0, 5.0, cell / max(sbMapTexel, 1e-9));
  if (w <= 0.002) continue;
  sbOctave(vSbPos, sbN, sbSunObj, cell, w, sbSeed + float(k) * 17.13, sbGrad, sbAlb, sbShade, sbCover);
  // Undulating ground and grain between the rocks and craters.
  vec4 sbNd = sbNoiseD(vSbPos / (cell * 0.7) + sbSeed + float(k) * 5.3);
  sbGrad += w * SB_GROUND * sbNd.yzw;
  sbAlb *= 1.0 + w * 0.12 * (sbNd.x - 0.5);
}
sbRel = mix(sbRel, sbRelC, sbCover);
sbGrad += sbMapGrad * (1.0 - sbCover);
float sbCast = 1.0;
if (dot(sbN, sbSunObj) > 0.0) sbCast = sbCastShadow(vSbPos, sbSunObj);
diffuseColor.rgb = sbAlbedo * sbRel * sbAlb;
`;

// Runs in place of normal_fragment_maps: the map's relief and the procedural
// slopes, both gathered in the body's frame.
const FRAGMENT_NORMAL = /* glsl */ `
{
  vec3 g = sbGrad - dot(sbGrad, sbN) * sbN;
  vec3 gv = sbObjToView * g;
  normal = normalize(normal - (gv - dot(gv, normal) * normal));
}
`;

// After the lights: the Sun's light shaped by Lommel-Seeliger and cut by
// the shadows. Ambient and sky light are left as they are.
const FRAGMENT_LIGHT = /* glsl */ `
{
  vec3 sbV = normalize(vViewPosition);
  vec3 sbL = normalize(sbSunView + vViewPosition);
  float mu0 = max(dot(normal, sbL), 0.0);
  float mu = max(dot(normal, sbV), 0.0);
  float ls = min(2.0 / max(mu0 + mu, 0.05), 4.0);
  reflectedLight.directDiffuse *= mix(1.0, ls, sbLS) * sbShade * sbCast;
}
`;

// opts: { albedo: THREE.Color (linear), map, mapGrey, relMax, validLat
// [south, north] radians, radiusTexture, reach, scale, ls, bump, cells
// (largest first), mapTexel (radians; the octaves the map can show stay out
// where it has data), mottle, ground (relief of the ground
// between rocks), seed, style, mobile }
export function createSmallBodyMaterial(opts) {
  const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, metalness: 0 });
  const cells = new Array(MAX_OCTAVES).fill(0);
  opts.cells.slice(0, MAX_OCTAVES).forEach((c, i) => { cells[i] = c; });
  const octaves = Math.max(1, Math.min(MAX_OCTAVES, opts.cells.length));
  const steps = opts.mobile ? 14 : 28;
  const reach = opts.reach * 1.02;
  const uniforms = {
    sbScale: { value: opts.scale },
    sbRadius: { value: opts.radiusTexture },
    sbMap: { value: opts.map || null },
    sbAlbedo: { value: opts.albedo.clone() },
    sbRelMax: { value: opts.relMax },
    sbValidLat: { value: new THREE.Vector2(opts.validLat ? opts.validLat[0] : -2, opts.validLat ? opts.validLat[1] : 2) },
    sbReach: { value: reach },
    sbGrow: { value: Math.pow((2.2 * reach) / 0.012, 1 / steps) },
    sbSunObj: { value: new THREE.Vector3(1, 0, 0) },
    sbSunView: { value: new THREE.Vector3() },
    sbObjToView: { value: new THREE.Matrix3() },
    sbLS: { value: opts.ls },
    sbBump: { value: opts.bump || 0 },
    sbSeed: { value: opts.seed || 0 },
    sbCells: { value: cells },
    sbMapTexel: { value: opts.mapTexel || 0 },
    sbMottle: { value: opts.mottle || 0 },
  };
  material.defines = {
    ...material.defines,
    SB_STYLE: opts.style,
    SB_MAP: opts.map ? 1 : 0,
    SB_MAP_GREY: opts.mapGrey ? 1 : 0,
    SB_STEPS: steps,
    SB_OCTAVES: octaves,
    SB_GROUND: (opts.ground ?? 0.1).toFixed(3),
  };
  material.userData.sbUniforms = uniforms;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${VERTEX_PARS}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${VERTEX_MAIN}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAGMENT_PARS}`)
      .replace('#include <map_fragment>', FRAGMENT_SURFACE)
      .replace('#include <normal_fragment_maps>', FRAGMENT_NORMAL)
      .replace('#include <lights_fragment_end>', `#include <lights_fragment_end>\n${FRAGMENT_LIGHT}`);
  };
  material.customProgramCacheKey = () => `smallbody:${opts.style}:${opts.map ? (opts.mapGrey ? 'g' : 'c') : 'n'}:${steps}:${octaves}:${material.defines.SB_GROUND}`;
  return material;
}
